import { useRef } from 'react';
import type { AvailabilityDay, Period, PortalRangeAnswer } from '@bbbk/schema';
import { G } from './glyphs';
import { BookBar, BookPage, RangeNotes } from './RequestChrome';
import { ReachFailure } from './ReachFailure';
import { PendingButton } from '../components/common/PendingButton';
import { cellClass, endInFocus, longDay, monthCells, monthStart, monthTitle, periodWord, shortDay, stepLead, type StayEnd } from './requestRules';

// `c6` and its nine states (flows 2.1 step 2, 2.2) — the calendar, the range
// said in words, the two handover times, and what the dates say back.
//
// THE SERVER PAINTS IT (AC3, AC24). Each cell's state, each note, which end is
// afternoon only, whether the primary is live — all of it arrives in the
// availability answer. This component lays a month out and draws what it was
// given; a day it was not told about is drawn plain and cannot be judged.

const DOW = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

export interface RequestDatesStepProps {
  /** The first of the month on screen. */
  month: string;
  today: string;
  /** The month's days as the server answered them; null before the first answer or after a failure. */
  days: AvailabilityDay[] | null;
  handoverAm: string;
  handoverPm: string;
  checkIn: string | null;
  checkOut: string | null;
  dropOff: Period | null;
  collection: Period | null;
  /** What the chosen range says back; null while nothing is chosen. */
  range: PortalRangeAnswer | null;
  loading: boolean;
  /** The calendar could not be read — not a 401, which never reaches here. */
  failure: boolean;
  onPick: (day: string) => void;
  onMonth: (delta: 1 | -1) => void;
  onPeriod: (end: 'dropOff' | 'collection', p: Period) => void;
  onNext: () => void;
  onBack: () => void;
  onRetry: () => void;
  onClose: () => void;
  /** The end the customer chose to change by tapping it, if any (Freddi, 2026-10-08). */
  chosenEnd: StayEnd | null;
  onChooseEnd: (end: StayEnd) => void;
}

/**
 * ONE END OF THE STAY, AND THE WAY TO CHANGE IT (Freddi, 2026-10-08). Tap it
 * and the calendar's next tap sets this end: the card lifts into focus, the
 * end's own day is ringed on the calendar, and the lead says what to do.
 * Collect has nothing to change until there is an arrival.
 */
function Side({ label, verb, day, period, focus, disabled, onChoose }: {
  label: string; verb: string; day: string | null; period: Period | null; focus: boolean; disabled: boolean; onChoose: () => void;
}) {
  return (
    <button type="button" className={'rg-side' + (focus ? ' on' : '')} aria-pressed={focus} disabled={disabled} aria-label={`Change the ${verb} day`} onClick={onChoose}>
      <span className="rg-l">{label}</span>
      <b className="rg-d">{day ? longDay(day) : '—'}</b>
      <span className="rg-t">{focus ? 'Tap a day' : !day ? 'pick a day' : period ? periodWord(period) : 'pick a time'}</span>
    </button>
  );
}

function PeriodSeg({ label, day, value, pmOnly, onPick }: { label: string; day: string | null; value: Period | null; pmOnly: boolean; onPick: (p: Period) => void }) {
  return (
    <div className="cp-field" style={{ flex: 1 }}>
      <label>{day ? `${label} · ${shortDay(day)}` : label}</label>
      <div className="cp-seg" role="radiogroup" aria-label={label}>
        <button type="button" role="radio" aria-checked={value === 'am'} disabled={!day || pmOnly} className={pmOnly ? 'off' : value === 'am' ? 'on' : ''} onClick={() => onPick('am')}>
          Morning
        </button>
        <button type="button" role="radio" aria-checked={value === 'pm'} disabled={!day} className={value === 'pm' ? 'on' : ''} onClick={() => onPick('pm')}>
          Afternoon
        </button>
      </div>
    </div>
  );
}

/** Six rows, always: a five-row month and a six-row one are the same height, so nothing below the calendar moves. */
const GRID = 42;

export function RequestDatesStep(p: RequestDatesStepProps) {
  const byDay = new Map((p.days ?? []).map((d) => [d.day, d]));
  const cells = monthCells(p.month);
  // WHICH WAY THE MONTH MOVED, kept with the month it belongs to, so the slide
  // survives the re-renders that follow it (a read landing, a pick) rather
  // than being cut off by the first of them.
  const turn = useRef<{ month: string; dir: '' | ' next' | ' prev' }>({ month: p.month, dir: '' });
  if (turn.current.month !== p.month) turn.current = { month: p.month, dir: p.month > turn.current.month ? ' next' : ' prev' };
  const nights =
    p.checkIn && p.checkOut ? Math.round((Date.parse(`${p.checkOut}T12:00:00Z`) - Date.parse(`${p.checkIn}T12:00:00Z`)) / 86_400_000) : null;
  const pmOnly = (day: string | null) => !!day && byDay.get(day)?.handover === 'pm_only';
  // READY IS THE ANSWER *AND* THE SCREEN. The last answer said yes, and what is
  // on screen is still a whole range with both times — a range begun again is
  // off at once, not held live by the answer for the range that went.
  const whole = !!(p.checkIn && p.checkOut && p.dropOff && p.collection);
  const ready = whole && !!p.range?.ready;
  // A READY BUTTON STAYS READY-COLOURED WHILE THE CALENDAR ANSWERS (Freddi,
  // 2026-10-08). Every pick re-reads; dropping to grey for the read and back
  // to teal when it landed was the flash. It goes busy instead, and cannot be
  // pressed until the answer for what is on screen is in.
  const busy = ready && p.loading;
  const live = ready && !p.loading;
  const canGoBack = p.month > monthStart(p.today);
  const picked = { checkIn: p.checkIn, checkOut: p.checkOut };
  const focus = endInFocus(picked, p.chosenEnd);
  const hasRange = !!p.checkIn && !!p.checkOut;

  return (
    <BookPage onSubmit={() => live && p.onNext()} onClose={p.onClose}>
      <BookBar step={2} onBack={p.onBack} />
      <h1 className="cp-h1">When?</h1>
      <p className="cp-p mute cp-lead" aria-live="polite">{stepLead(picked, p.chosenEnd)}</p>
      <p className="cp-eyebrow cs-eyebrow">Your dates</p>

      {/* NEVER FADED WHILE IT READS (Freddi, 2026-10-08): a calendar dimming on
          every tap and every month was the flash. The primary carries the wait. */}
      <div className={'cp-cal' + (hasRange || focus ? ' has-range' : '')} aria-busy={p.loading ? 'true' : undefined}>
        <div className="mh">
          <button type="button" className="cp-cal-nav" onClick={() => p.onMonth(-1)} disabled={!canGoBack} aria-label="Previous month">
            {G.chevLeft({ size: 16 })}
          </button>
          <span key={p.month} className={'mt' + turn.current.dir}>{monthTitle(p.month)}</span>
          <button type="button" className="cp-cal-nav" onClick={() => p.onMonth(1)} aria-label="Next month">
            {G.chevRight({ size: 16 })}
          </button>
        </div>
        <div className="dow">
          {DOW.map((d) => (
            <span key={d}>{d}</span>
          ))}
        </div>
        <div key={p.month} className={'days' + turn.current.dir}>
          {cells.map((day, i) => {
            if (!day) return <span key={`e${i}`} className="e"></span>;
            const a = byDay.get(day);
            // A month not read yet draws neutral and numbered, never blank; its
            // colours settle in when the answer lands.
            const cls = a ? cellClass(day, a, p.checkIn, p.checkOut, focus) : 'wait';
            // THE STAY AS ONE BAND: its two ends, and where a week ends and
            // begins — the band joins across the gaps and rounds where it
            // starts, stops, or wraps to the next row.
            const band = hasRange
              ? (day === p.checkIn ? ' from' : day === p.checkOut ? ' to' : '') + (i % 7 === 6 ? ' eow' : i % 7 === 0 ? ' sow' : '')
              : '';
            return (
              <button
                key={day}
                type="button"
                className={('d ' + cls + (/\b(sel|in)\b/.test(cls) ? band : '')).trim()}
                disabled={!a || a.level === 'past'}
                aria-pressed={day === p.checkIn || day === p.checkOut}
                aria-label={longDay(day) + (a?.mine ? ', your stay' : '')}
                onClick={() => p.onPick(day)}
              >
                {Number(day.slice(8, 10))}
                {/* A night of a stay they already have — its own element, so it
                    never fights the condition dot or the PM label for a slot. */}
                {a?.mine && <i className="own" aria-hidden="true" />}
              </button>
            );
          })}
          {Array.from({ length: Math.max(0, GRID - cells.length) }, (_, i) => (
            <span key={`t${i}`} className="e tail"></span>
          ))}
        </div>
        <div className="cp-key one">
          <span className="a">Limited</span>
          <span className="r">Full</span>
          <span className="x">No drop-off / collection</span>
          {/* Keyed only when one is in the month on screen — a key lists what is there. */}
          {(p.days ?? []).some((d) => d.mine) && <span className="m">Your stays</span>}
        </div>
      </div>

      {p.failure && <ReachFailure what="calendar" onRetry={p.onRetry} busy={p.loading} />}

      <p className="cp-eyebrow cs-eyebrow">Your stay</p>
      <div className={'cp-range' + (p.checkIn ? '' : ' empty')}>
        <Side label="Drop off" verb="drop-off" day={p.checkIn} period={p.dropOff} focus={focus === 'dropOff'} disabled={false} onChoose={() => p.onChooseEnd('dropOff')} />
        <div className="rg-mid">
          <span className="rg-n">{nights ?? '·'}</span>
          <span className="rg-u">{nights === 1 ? 'night' : 'nights'}</span>
        </div>
        <Side label="Collect" verb="collection" day={p.checkOut} period={p.collection} focus={focus === 'collection'} disabled={!p.checkIn} onChoose={() => p.onChooseEnd('collection')} />
      </div>

      <div className="cp-row cp-periods">
        <PeriodSeg label="Drop off" day={p.checkIn} value={p.dropOff} pmOnly={pmOnly(p.checkIn)} onPick={(v) => p.onPeriod('dropOff', v)} />
        <PeriodSeg label="Collect" day={p.checkOut} value={p.collection} pmOnly={pmOnly(p.checkOut)} onPick={(v) => p.onPeriod('collection', v)} />
      </div>

      <div className="cp-actions">
        {/* THE NOTES RIDE WITH THE BUTTON THEY EXPLAIN (Freddi, 2026-10-08:
            "only the full message is visible"). Below the content they sat
            under the pinned primary, and a phone showed one of three; in the
            pinned bar they are on screen whenever the button is, saying why it
            is live or not. One row each, so the bar stays short. */}
        {p.range && p.checkIn && p.checkOut && p.range.notes.length > 0 && <RangeNotes notes={p.range.notes} />}
        <PendingButton type="submit" className={'cp-btn primary' + (busy || live ? '' : ' off')} icon={G.calendar({ size: 15 })} busy={busy} disabled={!live}>
          Submit your dates
        </PendingButton>
      </div>
    </BookPage>
  );
}
